import { useRef, useState } from 'react';
import type { PhotoQuarterTurn, StoredPhotoManifest } from '@cer-autofill/shared';
import { canRotatePhoto } from './EditablePhotoTile.js';
import { PhotoThumb } from './PhotoThumb.js';

export interface ReportPhotoActions {
  onUploadPhoto?: ((promptKey: string, file: File) => Promise<void>) | undefined;
  onDeletePhoto?: ((photoId: string) => Promise<void>) | undefined;
  onRotatePhoto?: ((photoId: string, degrees: PhotoQuarterTurn) => Promise<void>) | undefined;
}

/** Evidence saves immediately; report text remains in the revision draft. */
export function ReportPhotoEditor({
  jobId,
  promptKey,
  photos,
  accept = 'image/*',
  uploadLabel = 'Add photos',
  uploadOnly = false,
  onOpenPhoto,
  onUploadPhoto,
  onDeletePhoto,
  onRotatePhoto,
}: {
  jobId: string;
  promptKey: string;
  accept?: string;
  uploadLabel?: string;
  /** Upload control only. The caller draws the gallery tiles itself. */
  uploadOnly?: boolean;
  photos: readonly StoredPhotoManifest[];
  onOpenPhoto: (photo: StoredPhotoManifest, url: string) => void;
} & ReportPhotoActions) {
  const saving = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(action: () => Promise<void>) {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Photo update failed');
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }
  const upload = (files: File[]) =>
    run(async () => {
      for (const file of files) {
        const allowed = accept
          .split(',')
          .some((type) =>
            type.trim().endsWith('/*')
              ? file.type.startsWith(type.trim().slice(0, -1))
              : file.type === type.trim(),
          );
        if (!allowed) throw new Error(`Unsupported file: ${file.name}`);
      }
      for (const file of files) await onUploadPhoto?.(promptKey, file);
    });
  return (
    <div
      onDragOver={(e) => {
        if (onUploadPhoto) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!onUploadPhoto) return;
        e.preventDefault();
        if (!saving.current) void upload(Array.from(e.dataTransfer.files));
      }}
    >
      {!uploadOnly && (
        <div className="photo-grid">
          {photos.map((photo) => (
            <div key={photo.photoId}>
              <PhotoThumb
                jobId={jobId}
                photoId={photo.photoId}
                promptKey={photo.promptKey}
                caption={photo.filename}
                mimeType={photo.mimeType}
                version={photo.uploadedAt}
                onOpen={(url) => onOpenPhoto(photo, url)}
              />
              {onRotatePhoto && canRotatePhoto(photo.mimeType) && (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    aria-label="Rotate left"
                    onClick={() => {
                      const rotate = onRotatePhoto;
                      void run(() => rotate(photo.photoId, 270));
                    }}
                  >
                    Rotate ↺
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    aria-label="Rotate right"
                    onClick={() => {
                      const rotate = onRotatePhoto;
                      void run(() => rotate(photo.photoId, 90));
                    }}
                  >
                    Rotate ↻
                  </button>
                </>
              )}
              {onDeletePhoto && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void run(() => onDeletePhoto(photo.photoId))}
                >
                  Delete {photo.filename}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {onUploadPhoto && (
        <label>
          {uploadLabel} — or drop files here
          <input
            type="file"
            accept={accept}
            multiple
            disabled={busy}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              void upload(files);
            }}
          />
        </label>
      )}
      {busy && <p role="status">Saving photos…</p>}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
